// Notification previews for a new, rescheduled or cancelled change (CAL 03).
// A preview is only ever shown on the page. Nothing here sends anything.

export type ChangeEvent = 'created' | 'rescheduled' | 'cancelled';

export interface PreviewEntry {
  title: string;
  systems: string[];
  scheduledStart: string;
  timezone: string;
  impact: string;
  downtime: string;
  userAction: string;
  risk: string;
  state?: string;
  previousStart?: string;
  cancelReason?: string;
}

export interface Counts {
  total: number;
  all: number;
  bySystem: Record<string, number>;
  audience: number;
}

export interface Preview {
  label: string;
  subject: string;
  lines: string[];
  audience: { total: number; perSystem: Array<{ system: string; count: number }>; allSystems: number };
}

export const PREVIEW_LABEL = 'Preview only. Nothing is sent from this proof of concept.';

export function formatWhen(iso: string, timezone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const text = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
  return `${text} (${timezone})`;
}

export function buildPreview(event: ChangeEvent, entry: PreviewEntry, counts: Counts): Preview {
  const when = formatWhen(entry.scheduledStart, entry.timezone);
  const systems = entry.systems.join(', ');
  const subjects: Record<ChangeEvent, string> = {
    created: `Upcoming change: ${entry.title}`,
    rescheduled: `Rescheduled: ${entry.title}`,
    cancelled: `Cancelled: ${entry.title}`,
  };
  const lines =
    event === 'cancelled'
      ? [`This change to ${systems} will not go ahead.`, ...(entry.cancelReason ? [`Reason: ${entry.cancelReason}`] : []), 'No action is needed.']
      : [
          ...(event === 'rescheduled' && entry.previousStart
            ? [`Moved from ${formatWhen(entry.previousStart, entry.timezone)} to ${when}.`]
            : [`When: ${when}`]),
          `Systems: ${systems}`,
          `What changes: ${entry.impact}`,
          `Downtime: ${entry.downtime}`,
          `What you need to do: ${entry.userAction}`,
          `Risk: ${entry.risk}`,
        ];
  return {
    label: PREVIEW_LABEL,
    subject: subjects[event],
    lines,
    audience: {
      total: counts.audience,
      allSystems: counts.all,
      perSystem: entry.systems.map((system) => ({ system, count: counts.bySystem[system] ?? 0 })),
    },
  };
}
